import React from "react";
import { Alert, Divider, Table, Tag } from "antd";
import * as XLSX from "xlsx";
import {
  CheckCircleOutlined,
  MinusCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { ModalItem, ButtonItem } from "../../../../../Components/Items";

const COLUMNAS_EXCEL = [
  "Fila",
  "Matricula",
  "Alumno",
  "Lista",
  "Libro",
  "Cantidad",
  "Pidio en el formulario",
  "Estado",
  "Folio",
  "Codigo",
  "Correo",
  "Correo enviado",
  "Motivo",
];

const ETIQUETA_ESTADO = {
  creada: "Venta creada",
  omitida: "Omitida",
  error: "Error",
};

const descargarExcel = (resultados) => {
  const filas = resultados.map((resultado) => ({
    Fila: resultado.numeroFila || "",
    Matricula: resultado.matricula || "",
    Alumno: resultado.alumno || "",
    Lista: resultado.lista || "",
    Libro: resultado.insumo || "",
    Cantidad: resultado.cantidad || "",
    "Pidio en el formulario": resultado.material || "",
    Estado: ETIQUETA_ESTADO[resultado.estado] || resultado.estado || "",
    Folio: resultado.folio || "",
    Codigo: resultado.codigo || "",
    Correo: resultado.correo || "",
    "Correo enviado": resultado.correoEnviado ? "Si" : "No",
    Motivo: resultado.motivo || "",
  }));

  const hoja = XLSX.utils.json_to_sheet(filas, { header: COLUMNAS_EXCEL });
  hoja["!cols"] = [
    { wch: 8 },
    { wch: 16 },
    { wch: 30 },
    { wch: 24 },
    { wch: 30 },
    { wch: 10 },
    { wch: 30 },
    { wch: 14 },
    { wch: 14 },
    { wch: 22 },
    { wch: 30 },
    { wch: 14 },
    { wch: 50 },
  ];

  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, hoja, "Resultados");
  XLSX.writeFile(libro, "resultadosVentasLibros.xlsx");
};

const ResultadosImportacionModal = ({ visible, resultado, cerrarModal }) => {
  const resultados = resultado?.resultados || [];

  const creadas = resultados.filter((r) => r.estado === "creada");
  const omitidas = resultados.filter((r) => r.estado === "omitida");
  const errores = resultados.filter((r) => r.estado === "error");
  const sinCodigo = creadas.filter((r) => !r.codigo);
  const sinCorreo = creadas.filter((r) => r.codigo && !r.correoEnviado);

  const resumen = [
    {
      key: "creadas",
      label: "Ventas creadas",
      count: creadas.length,
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
    },
    {
      key: "omitidas",
      label: "Omitidas (ya tenían venta)",
      count: omitidas.length,
      icon: <MinusCircleOutlined style={{ color: "#8c8c8c" }} />,
    },
    {
      key: "errores",
      label: "Con error",
      count: errores.length,
      icon: <CloseCircleOutlined style={{ color: "#ff4d4f" }} />,
    },
  ];

  const columnas = [
    { title: "Fila", dataIndex: "numeroFila", key: "numeroFila", width: 60 },
    { title: "Matrícula", dataIndex: "matricula", key: "matricula", width: 120 },
    { title: "Alumno", dataIndex: "alumno", key: "alumno", width: 200 },
    { title: "Libro", dataIndex: "insumo", key: "insumo", width: 220 },
    {
      title: "Estado",
      key: "estado",
      width: 130,
      render: (_, record) => {
        if (record.estado === "creada")
          return <Tag color="green">Venta creada</Tag>;
        if (record.estado === "omitida") return <Tag>Omitida</Tag>;
        return <Tag color="red">Error</Tag>;
      },
    },
    { title: "Folio", dataIndex: "folio", key: "folio", width: 120 },
    { title: "Código", dataIndex: "codigo", key: "codigo", width: 180 },
    {
      title: "Correo",
      key: "correoEnviado",
      width: 100,
      align: "center",
      render: (_, record) =>
        record.estado === "creada" ? (
          <Tag color={record.correoEnviado ? "green" : "orange"}>
            {record.correoEnviado ? "Enviado" : "No"}
          </Tag>
        ) : null,
    },
    { title: "Motivo", dataIndex: "motivo", key: "motivo", width: 320 },
  ];

  return (
    <ModalItem
      title="Resultado de la importación"
      visible={visible}
      spinning={false}
      onCancel={cerrarModal}
      width={1100}
    >
      {resultado && (
        <>
          {resultado.incompleto && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 12 }}
              message="La importación se detuvo antes de terminar"
              description={`${resultado.mensajeError || "Se interrumpió el envío"}. Las filas que no aparecen abajo no se procesaron; puedes volver a subir el mismo archivo y sólo se crearán las que falten.`}
            />
          )}

          <div>
            {resumen.map((fila) => (
              <div
                key={fila.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 0",
                }}
              >
                {fila.icon}
                <span style={{ flex: 1, color: "#595959" }}>{fila.label}</span>
                <span style={{ fontWeight: 500 }}>{fila.count}</span>
              </div>
            ))}
          </div>

          {sinCodigo.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message={`${sinCodigo.length} ventas quedaron sin código`}
              description="No había lotes disponibles del libro. La venta ya existe: carga los códigos al inventario y entrégalos desde la venta (ahí se manda el correo)."
            />
          )}

          {sinCorreo.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message={`${sinCorreo.length} ventas con código pero sin correo enviado`}
              description="Revisa el motivo de cada una en la tabla; el código ya está asignado a la venta."
            />
          )}

          <Divider style={{ margin: "12px 0" }} />

          <Table
            size="small"
            rowKey={(record, index) => index}
            dataSource={resultados}
            columns={columnas}
            scroll={{ x: "max-content", y: 360 }}
            pagination={{ pageSize: 50, showSizeChanger: false }}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 16,
            }}
          >
            <ButtonItem
              onClick={() => descargarExcel(resultados)}
              disabled={resultados.length === 0}
            >
              Descargar Resultados
            </ButtonItem>
            <ButtonItem type="primary" onClick={cerrarModal}>
              Aceptar
            </ButtonItem>
          </div>
        </>
      )}
    </ModalItem>
  );
};

export default ResultadosImportacionModal;
